/* eslint-disable @typescript-eslint/no-unused-expressions */
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
  FocusEvent,
} from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { ContactPhone, SupplierGroup } from "models/suppliergroups"
import { IconButton, InputLabel } from "@material-ui/core"
// import { useParams } from "react-router-dom"
import { queryCache, useMutation, useQuery } from "react-query"

import AutoCompleteInput from "components/inputs/AutoComplete"
import { ReactComponent as DeleteIcon } from "assets/delete-grey.svg"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import FormControl from "@material-ui/core/FormControl"
import { Loading } from "components/Loading"
import MenuItem from "@material-ui/core/MenuItem"
import Select from "@material-ui/core/Select"
import { TextInput } from "components/inputs/TextInput"
import { WithAuthority } from "components/WithAuthority"
import assoc from "lodash/fp/set"
import { fetchCountries } from "api/geography"
import get from "lodash/fp/get"
import { patchSupplier } from "api/suppliergroups"
import { styles } from "./styles"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

type Props = {
  data?: SupplierGroup
  loading?: boolean
}

type CountryOption = { name: string; code: string }

const InitialState = {
  id: "",
  name: "",
  code: "",
  contact: {},
}

function Profile(props: Props) {
  const { t } = useI18n()
  // const { id } = useParams<{ id: string }>()
  const { data, loading } = props
  const [state, setState] = useState<SupplierGroup>(() => InitialState)
  const [errors, setErrors] = useState<ErrorsMap | undefined>()
  const { hasAuthority } = useAuthenticated()

  const [patch] = useMutation(patchSupplier, {
    onSuccess: () => {
      queryCache.invalidateQueries("supplier-group")
      queryCache.invalidateQueries("supplier-groups")
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handleChange = (
    evt: ChangeEvent<
      | HTMLInputElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
  ) => {
    const { name, value } = evt?.target
    !!name && setErrors((e) => assoc(name, undefined, e || {}))
    !!name && setState((s) => assoc(name, value, s))
  }

  const handleBlur = useCallback(
    (section?: "contact" | "address") =>
      (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name = "", value = "" } = evt?.target ?? {}
        !!data &&
          value !== get(name, data) &&
          patch({
            id: data?.id,
            [section || name]: state[section || (name as keyof SupplierGroup)],
          })
      },
    [patch, data, state],
  )

  const handleCountryChange = useCallback(
    (countryCode?: string) => {
      !!countryCode &&
        patch({ id: data?.id, address: { ...state.address, countryCode } })
    },
    [patch, data, state],
  )

  useEffect(() => {
    if (!!data) {
      setState(() => data)
    }
  }, [data])

  const handleAddPhone = useCallback(() => {
    const currentPhones = state.contact?.phones || []
    const phones = [
      ...currentPhones,
      { number: "", type: "Mobile", extension: "" },
    ] as ContactPhone[]
    setState((state) => ({ ...state, contact: { ...state.contact, phones } }))
  }, [state])

  const statePhones = state?.contact?.phones
  const dataPhones = data?.contact?.phones

  const handleDeletePhone = useCallback(
    (idx: number) => {
      const phones = state.contact?.phones?.filter((_, id) => id !== idx) || []
      statePhones?.length === dataPhones?.length &&
        patch({
          id: data?.id,
          contact: { ...state?.contact, phones },
        })
      setState((s) => ({ ...s, contact: { ...s.contact, phones } }))
    },
    [state.contact, statePhones?.length, dataPhones?.length, patch, data?.id],
  )

  const { data: countries } = useQuery(["countries"], fetchCountries, {
    staleTime: 120000,
  })

  const countrySelected = countries?.data.find(
    (country) => country.code === state?.address?.countryCode,
  ) || { name: "", code: "" }

  return loading ? (
    <Loading />
  ) : (
    <div className={styles.main}>
      <section className={styles.code}>
        <span className={styles.sectionTitle}>
          {t("supplier-groups.profile.details")}
        </span>
        <TextInput
          name="code"
          value={state?.code || ""}
          label={t("supplier-groups.list.code")}
          className={styles.titleInput}
          onChange={handleChange}
          error={!!errors && !!errors["code"]}
          errorMessage={!!errors ? errors["code"] : ""}
          onBlur={(e) => handleBlur()(e)}
          margin="normal"
          size="medium"
          disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
        />
      </section>
      <section className={styles.section}>
        <span className={styles.sectionTitle}>
          {t("supplier-groups.profile.addresses")}
        </span>
        <div className={styles.addresses}>
          {t("supplier-groups.profile.business-address")}
        </div>
        <div className={styles.inputsWrapper}>
          <div className={styles.address}>
            <div>
              <TextInput
                name="address.address1"
                value={state?.address?.address1 || ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                fullWidth
                label={t("supplier-groups.profile.address1")}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
              />
            </div>
            <div>
              <TextInput
                name="address.address2"
                value={state?.address?.address2 || ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                fullWidth
                label={t("supplier-groups.profile.address2")}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
              />
            </div>
            <div className={styles.dropdownInput}>
              <AutoCompleteInput<CountryOption>
                label={t("supplier.profile.country")}
                size="medium"
                margin="normal"
                options={countries?.data}
                onChange={(val) => {
                  handleCountryChange(val?.code)
                }}
                getOptionSelected={(option, value) => {
                  return option.code === value.code
                }}
                renderOption={(option) => (
                  <span>
                    {option.name} ({option.code})
                  </span>
                )}
                getOptionLabel={(option) =>
                  option.name && `${option.name} (${option.code})`
                }
                value={countrySelected || ""}
                fetchOnFilter={false}
                error={Boolean(errors?.countryCode)}
                helperText={errors?.countryCode}
                disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
              />
              <TextInput
                name="address.state"
                value={state?.address?.state || ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                label={t("supplier-groups.profile.state")}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
              />
              <TextInput
                name="address.city"
                value={state?.address?.city || ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                label={t("supplier-groups.profile.city")}
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
              />
              <TextInput
                name="address.zipCode"
                value={state?.address?.zipCode || ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                label={t("supplier-groups.profile.zipCode")}
                className={styles.zip}
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
              />
            </div>
          </div>
        </div>
      </section>
      <section className={styles.billingSection}>
        <span className={styles.sectionTitle}>
          {t("supplier-groups.profile.billing-contact")}
        </span>
        <div className={styles.billingDetails}>
          {t("supplier-groups.profile.contact-info")}
        </div>
        <div className={styles.dropdownInput}>
          <TextInput
            name="contact.firstName"
            value={state?.contact?.firstName || ""}
            onChange={handleChange}
            error={!!errors && !!errors["contact.firstName"]}
            errorMessage={!!errors ? errors["contact.firstName"] : ""}
            onBlur={(e) => handleBlur("contact")(e)}
            label={t("supplier-groups.profile.title")}
            margin="normal"
            size="medium"
            disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
          />
          <TextInput
            name="contact.lastName"
            value={state?.contact?.lastName || ""}
            onChange={handleChange}
            error={!!errors && !!errors["contact.lastName"]}
            errorMessage={!!errors ? errors["contact.lastName"] : ""}
            onBlur={(e) => handleBlur("contact")(e)}
            label={t("supplier-groups.profile.name")}
            className={styles.nameInput}
            margin="normal"
            size="medium"
            disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
          />
        </div>
        <TextInput
          name="contact.email"
          value={state?.contact?.email || ""}
          onChange={handleChange}
          error={!!errors && !!errors["contact.email"]}
          errorMessage={!!errors ? errors["contact.email"] : ""}
          onBlur={(e) => handleBlur("contact")(e)}
          label={t("supplier-groups.profile.email")}
          className={styles.mailInput}
          margin="normal"
          size="medium"
          disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
        />
        {state.contact?.phones?.map((phone, idx) => (
          <div className={styles.inputsWrapper} key={idx}>
            <div>
              <FormControl
                fullWidth
                margin="normal"
                size="medium"
                variant="outlined"
              >
                <InputLabel>{t("supplier-groups.profile.ntype")}</InputLabel>
                <Select
                  fullWidth
                  IconComponent={DropdownIcon}
                  id="type"
                  name={`contact.phones.${idx}.type`}
                  value={phone?.type || "Mobile"}
                  onChange={handleChange}
                  onBlur={(e) => handleBlur("contact")(e)}
                  labelId="phone-type-input-label"
                  label={t("supplier-groups.profile.ntype")}
                  disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
                  className={styles.number}
                  classes={{
                    icon: styles.selectIcon,
                  }}
                >
                  <MenuItem key="mobile" value="Mobile">
                    {t("inputs.phonetype.mobile")}
                  </MenuItem>
                  <MenuItem key="business" value="Business">
                    {t("inputs.phonetype.business")}
                  </MenuItem>
                  <MenuItem key="home" value="Home">
                    {t("inputs.phonetype.home")}
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
            <TextInput
              id="number"
              name={`contact.phones.${idx}.number`}
              type="number"
              value={phone?.number || ""}
              onChange={handleChange}
              onBlur={(e) => handleBlur("contact")(e)}
              label={t("supplier-groups.profile.number")}
              className={styles.phoneInput}
              margin="normal"
              size="medium"
              disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
            />
            <TextInput
              id="extension"
              name={`contact.phones.${idx}.extension`}
              value={phone?.extension || ""}
              onChange={handleChange}
              onBlur={(e) => handleBlur("contact")(e)}
              label={t("supplier-groups.profile.extension")}
              className={styles.codeInput}
              margin="normal"
              size="medium"
              disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER_GROUP"])}
            />
            {idx > 0 ? (
              <WithAuthority allowed={["AUTH_EDIT_SUPPLIER_GROUP"]}>
                <IconButton
                  className={styles.iconButton}
                  onClick={() => handleDeletePhone(idx)}
                >
                  <DeleteIcon className={styles.deleteIcon} color="inherit" />
                </IconButton>
              </WithAuthority>
            ) : null}
          </div>
        ))}
        <WithAuthority allowed={["AUTH_EDIT_SUPPLIER_GROUP"]}>
          <span className={styles.addPhone} onClick={handleAddPhone}>
            {t("supplier-groups.profile.add-phone")}
          </span>
        </WithAuthority>
      </section>
    </div>
  )
}

export default Profile
